import { useRef, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { JsonView, Panel, StatusPill } from "@/components/kit";

const Checks = ({ checks }) => (
  <ul className="mt-3 space-y-1.5" data-testid="github-checks">
    {Object.entries(checks).map(([k, v]) => (
      <li key={k} className="flex items-center justify-between gap-3 text-xs"><span>{k.replace(/_/g, " ")}</span><StatusPill status={v ? "passed" : "failed"} /></li>
    ))}
  </ul>
);

const btn = "inline-flex items-center gap-2 rounded-sm px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50";

export const GitHubPanel = ({ gh, onChange }) => {
  const call = useApi();
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [replacing, setReplacing] = useState(false);
  const installed = !!gh.private_key_source;

  const pick = (files) => { setFileName(files?.[0]?.name || ""); setResult(null); };
  const onDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer.files?.length) return;
    file.current.files = e.dataTransfer.files;
    pick(e.dataTransfer.files);
  };
  const upload = async () => {
    const f = file.current?.files?.[0];
    if (!f) return toast.error("Choose the .pem file first");
    setBusy(true);
    try {
      const r = await call("/github/private-key", { method: "POST", body: { pem: await f.text() } });
      setResult(r.verification);
      toast.success("Private key verified and stored encrypted");
      setReplacing(false);
      onChange();
    } catch (e) { toast.error(e.message); setResult({ error: e.message }); }
    file.current.value = "";
    setFileName("");
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { setResult(await call("/github/verify")); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const remove = async () => {
    try { await call("/github/private-key", { method: "DELETE" }); toast.success("Uploaded key removed"); setResult(null); onChange(); }
    catch (e) { toast.error(e.message); }
  };
  const checks = result?.verification?.checks || result?.checks;

  return (
    <Panel title="GitHub App" aside={<StatusPill status={gh.status} testId="status-github" />} testId="github-panel">
      <p className="text-sm">Repository <code className="text-xs" data-testid="github-repository">{gh.repository}</code></p>
      <p className="mt-1 text-xs text-[var(--cc-muted)]">App {gh.app_id || "—"} · Installation {gh.installation_id || "—"} · Contents: read · Metadata: read · Writes disabled</p>
      <p className="mt-1 text-[11px] text-[var(--cc-muted)]" data-testid="github-config-source">Target pinned by {gh.configuration_source}</p>
      {gh.ignored_environment_values?.length > 0 && <p className="mt-1 text-[11px] text-[#8A5B0F]" data-testid="github-ignored-env">Stale environment values ignored: {gh.ignored_environment_values.join(", ")}. Update them in Secrets for tidiness. They are not used.</p>}
      <p className="mt-1 break-all text-xs" data-testid="github-key-status">Private key: {gh.private_key_source ? `${gh.private_key_source.replace(/_/g, " ")} · ${gh.private_key_fingerprint}` : "not installed"}</p>
      {installed && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[var(--cc-ok)]/40 bg-[#0F8B6D]/5 p-3" data-testid="github-key-installed">
          <div className="flex items-center gap-2 text-sm"><ShieldCheck size={18} className="text-[var(--cc-ok)]" />
            <div><p className="font-semibold text-[var(--cc-navy)]">Private key installed and saved</p>
              <p className="break-all text-[11px] text-[var(--cc-muted)]">{gh.private_key_source === "environment_secret" ? "From the environment secret" : "Stored encrypted in this environment's database"}. It stays across reloads and restarts · {gh.private_key_fingerprint}</p></div></div>
          {gh.private_key_source === "encrypted_upload" && <button onClick={() => setReplacing(!replacing)} data-testid="github-key-replace-toggle" className={`${btn} border border-[var(--cc-line)] text-[var(--cc-navy)] hover:border-[var(--cc-navy)]`}>{replacing ? "Cancel" : "Replace key"}</button>}
        </div>)}
      {gh.private_key_source !== "environment_secret" && (!installed || replacing) && (
        <div className="mt-4 rounded-sm border border-dashed border-[var(--cc-line)] p-3">
          <p className="mb-1 text-sm font-semibold text-[var(--cc-navy)]">Upload GitHub App private key (.pem)</p>
          <p className="mb-3 text-xs text-[var(--cc-muted)]">This is the file GitHub downloaded when you clicked "Generate a private key" (GitHub → Settings → Developer settings → GitHub Apps → your app → Private keys). Its name usually ends in <code>.private-key.pem</code> and it is normally in your Downloads folder. The gateway checks it with GitHub, encrypts it before storing it, and never shows it again or writes it to logs.</p>
          <label htmlFor="gh-pem" data-testid="github-key-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            className="flex cursor-pointer focus-within:ring-2 focus-within:ring-[var(--cc-signal)] flex-col items-center justify-center gap-1 rounded-sm border-2 border-dashed border-[var(--cc-navy)]/40 bg-[var(--cc-paper)] px-4 py-6 text-center transition-colors hover:border-[var(--cc-signal)]">
            <input id="gh-pem" ref={file} type="file" accept=".pem,application/x-pem-file,application/x-x509-ca-cert,text/plain" data-testid="github-key-file" className="sr-only" onChange={(e) => pick(e.target.files)} />
            <KeyRound size={22} className="text-[var(--cc-navy)]" />
            <span className="text-sm font-medium text-[var(--cc-navy)]">{fileName || "Click to choose (or drag in) your .pem file"}</span>
            <span className="text-[11px] text-[var(--cc-muted)]">{fileName ? "Ready. Click Verify & install key" : "Step 1 of 2"}</span>
          </label>
          <button onClick={upload} disabled={busy} data-testid="github-key-upload" className={`${btn} mt-3 bg-[var(--cc-navy)] text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)]`}><KeyRound size={14} />Step 2: Verify &amp; install key</button>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={verify} disabled={busy} data-testid="github-verify-button" className={`${btn} border border-[var(--cc-navy)] text-[var(--cc-navy)] hover:bg-[var(--cc-navy)] hover:text-white`}><ShieldCheck size={14} />Verify read-only access</button>
        {gh.private_key_source === "encrypted_upload" && <button onClick={remove} data-testid="github-key-remove" className={`${btn} border border-[var(--cc-line)] text-[var(--cc-deny)] hover:border-[var(--cc-deny)]`}><Trash2 size={14} />Remove key</button>}
      </div>
      {result?.error && <p className="mt-3 text-xs text-[var(--cc-deny)]" data-testid="github-verify-error">{result.error}</p>}
      {result?.qa_run_id && <p className="mt-3 text-xs text-[var(--cc-ok)]" data-testid="github-qa-run-id">Recorded as QA evidence {result.qa_run_id} (Independent QA page)</p>}
      {checks && <Checks checks={checks} />}
      {result?.tools?.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="github-tool-results">
          {result.tools.map((t) => <li key={t.tool} className="flex items-center justify-between gap-3 font-mono text-[11px]"><span>{t.tool} · audit #{t.audit_seq}</span><StatusPill status={t.status} /></li>)}
        </ul>
      )}
      {(result?.verification || result?.app) && <details className="mt-3"><summary className="cursor-pointer text-[11px] text-[var(--cc-info)]">Raw verification evidence</summary><JsonView value={result.verification || result} max="max-h-60" /></details>}
    </Panel>
  );
};
